import AssetViewModel from "@/domain/asset/AssetViewModel";
import AssetHistoryViewModel from "@/domain/asset/AssetHistoryViewModel";
import DualloAssetClient from "@/clients/duallo/DualloAssetClient";
import HotSpotService from "@/services/HotSpotService";
import DualloInspectionZoneClient from "@/clients/duallo/DualloInspectionZoneClient";
import DualloMarkerClient from "@/clients/duallo/DualloMarkerClient";
import DualloCameraHelperClient from "@/clients/duallo/DualloCameraHelperClient";
import ModelVersionService from "@/services/ModelVersionService";

class AssetService {

    async getAssetsByOrganizationIdAsync(organizationId) {
        return await DualloAssetClient.getAssetsByOrganizationId(organizationId).then(assets => assets.map(asset => new AssetViewModel(asset)));
    }

    async getAssetByIdAsync(organizationId, assetId) {
        return await DualloAssetClient.getAssetById(organizationId, assetId);
    }

    async createAssetAsync(newAsset) {
        return await DualloAssetClient.createAssetAsync(newAsset);
    }

    async getAnomalyMarkersByAssetId(assetId) {
        return await DualloMarkerClient.getMarkersByAssetId(assetId);
    }

    async getAnomalyMarkersByScanId(scanId) {
        return await DualloMarkerClient.getMarkersByScanId(scanId);
    }

    async updateAnomalyDetails(scanId, markerId, { name, externalReference }) {
        return await DualloMarkerClient.updateMarkerDetails(scanId, markerId, { name, externalReference });
    }

    // status: 'Approved' | 'Rejected'
    async reviewAnomaly(scanId, markerId, status) {
        return await DualloMarkerClient.updateMarkerStatus({ scanId, id: markerId, status });
    }

    async getAnomalyMarkersCount(assetId) {
        const markers = await this.getAnomalyMarkersByAssetId(assetId);
        return markers.length;
    }

    // Anomaly breakdown for an asset's CURRENT status: only its current model version and the scans
    // included in that version, counting active markers only. This mirrors what the viewer loads
    // (versionScanIds + markerStore's isActive filter), so the summary matches the in-asset numbers
    // rather than every marker ever created across old versions/scans.
    async getAnomalyBreakdownAsync(assetId) {
        const empty = { total: 0, pending: 0, accepted: 0, rejected: 0 };

        const version = await ModelVersionService.getCurrentModelVersion(assetId).catch(() => null);
        if (!version) return { ...empty };   // not scanned/processed yet → nothing live

        const scanIds = (version.scans ?? []).map(s => s.scanId).filter(Boolean);
        const ids = scanIds.length ? scanIds : [version.associatedScan].filter(Boolean);
        if (!ids.length) return { ...empty };

        const markersByScan = await Promise.all(
            ids.map(id => this.getAnomalyMarkersByScanId(id).catch(() => []))
        );

        const breakdown = { ...empty };
        for (const markers of markersByScan) {
            for (const marker of markers ?? []) {
                if (marker.isActive !== true) continue;   // active-only, as the viewer loads them
                breakdown.total++;
                if (marker.status === 'Pending') breakdown.pending++;
                else if (marker.status === 'Approved') breakdown.accepted++;
                else if (marker.status === 'Rejected') breakdown.rejected++;
            }
        }
        return breakdown;
    }

    // Org-wide anomaly totals for the home dashboard. No server-side aggregate endpoint exists, so we
    // fan out the per-asset breakdown and sum. A single asset failing (deleted mid-flight, no markers,
    // permission) degrades to zeros for that asset rather than failing the whole dashboard. In-flight
    // GET de-dup in ClientBase collapses any duplicate marker fetches.
    async getOrganizationAnomalyBreakdownAsync(assetIds) {
        const empty = { total: 0, pending: 0, accepted: 0, rejected: 0 };

        const breakdowns = await Promise.all(
            (assetIds ?? []).map(id =>
                this.getAnomalyBreakdownAsync(id).catch(error => {
                    if (process.env.NODE_ENV === 'development') {
                        console.error(`Failed to load anomaly breakdown for asset ${id}`, error);
                    }
                    return { ...empty };
                })
            )
        );

        return breakdowns.reduce((acc, b) => ({
            total: acc.total + b.total,
            pending: acc.pending + b.pending,
            accepted: acc.accepted + b.accepted,
            rejected: acc.rejected + b.rejected,
        }), { ...empty });
    }

    async getAnomalyMarkerCountByType(assetId) {
        const markers = await this.getAnomalyMarkersByAssetId(assetId);

        const groupedMarkers = Object.entries(
            markers.reduce((acc, marker) => {
                const type = marker.label;
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {})
        ).map(([name, value]) => ({ name, value }));

        return groupedMarkers;
    }

    // Same grouping, but only counts accepted (approved) anomalies.
    async getAcceptedAnomalyMarkerCountByType(assetId) {
        const markers = await this.getAnomalyMarkersByAssetId(assetId);

        const groupedMarkers = Object.entries(
            markers.reduce((acc, marker) => {
                const status = (marker.status || '').toLowerCase();
                if (status !== 'accepted' && status !== 'approved') return acc;
                const type = marker.label;
                acc[type] = (acc[type] || 0) + 1;
                return acc;
            }, {})
        ).map(([name, value]) => ({ name, value }));

        return groupedMarkers;
    }

    // Dashboard cards dispatch to these by name (service_name: AssetService); the underlying
    // hotspot fetch is owned by HotSpotService.
    async getHotSpotMarkersDashboardAsync(assetId) {
        const hotspots = await HotSpotService.getHotSpotMarkersByAssetId(assetId);

        return {
            hotSpots: hotspots
        };
    }

    async getHotSpotMarkersCount(assetId) {
        const hotSpots = await HotSpotService.getHotSpotMarkersByAssetId(assetId);
        return hotSpots.length;
    }

    async getTopThreeInspectionAreasByScore(assetId) {
        try {
            const areas = await DualloInspectionZoneClient.getInspectionAreasByScanId(assetId);
            return areas
                .sort((a, b) => b.integrityScore - a.integrityScore) // orders descending
                .slice(0, 3)
                .map(m => ({
                    key: m.name,
                    value: m.integrityScore
                }));    // picks top 3 records
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.log("DEV: ", error);
            }
        }
    }

    async getBottomThreeInspectionAreasByScore(assetId) {
        try {
            const areas = await DualloInspectionZoneClient.getInspectionAreasByScanId(assetId);
            return areas
                .sort((a, b) => a.integrityScore - b.integrityScore) // orders descending
                .slice(0, 3)
                .map(m => ({
                    key: m.name,
                    value: m.integrityScore
                }));    // picks top 3 records
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.log("DEV: ", error);
            }
        }
    }

    async getAssetIntegrityScore(assetId) {
        const inspectionAreas = await DualloInspectionZoneClient.getInspectionAreasByScanId(assetId);

        if (inspectionAreas.length == 0)
            return 0;

        const score = inspectionAreas.reduce((sum, area) => sum + (area.integrityScore || 0), 0);
        return Math.round(score / inspectionAreas.length) * 20;
    }

    async getCameraHelpersAsync(scanId) {
        return await DualloCameraHelperClient.getCameraHelpersByScanId(scanId);
    }

    async getAssetHistory(assetId) {
        try {
            const response = DualloAssetClient.getAssetHistory(assetId);

            let records = [];
            response.forEach(h => {
                const history = new AssetHistoryViewModel(h);
                records.push(history);
            });

            return records;
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.error("DEV: Failed to get asset history records.", error);
            }
            throw error;
        }
    }

    async getHistoricalAssetIntegrityScore(assetId) {
        try {
            const records = DualloAssetClient.getAssetHistory(assetId);

            const formattedRecords = records.map(rec => ({
                x: rec.createdOn,
                y: rec.integrityScore * 20
            }));

            return formattedRecords;

        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.log("DEV: Failed to get asset historical integrity score", error);
            }
            throw error;
        }
    }

    async getHistoricalAssetMarkerCount(assetId) {
        try {
            const records = DualloAssetClient.getAssetHistory(assetId);

            const formattedRecords = records.map(rec => ({
                x: rec.createdOn,
                y: rec.numberOfMarkers
            }));

            return formattedRecords;

        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.log("DEV: Failed to get asset historical marker count", error);
            }
            throw error;
        }
    }

    async updateAssetRecord(record) {
        return await DualloAssetClient.updateAsset(record);
    }

    async updateAssetProfileImage(updateImageRequest) {
        return await DualloAssetClient.updateAssetProfileImage(updateImageRequest);
    }

    async deleteAssetAsync(organizationId, assetId, isActive) {
        return await DualloAssetClient.deleteAsset(organizationId, assetId, isActive);
    }
}

export default new AssetService();